import type { SchoolDistrict } from '@/app/data/school-districts';

/*
 * The legacy lead form as a guided chat, as data. The page's questions come from the legacy slug page it
 * mirrors (legacy-form.ts builds a ChatForm from that page's panels on the server): the same questions,
 * answers, answer keys, order and conditions (renderWhen, the K-12 school district rules), asked one per
 * screen in the test prep chat's style. Each step says a few lines (the last one is the question), takes
 * one kind of answer, can be skipped, and can reply with a line or two (and a card). The page
 * (chat-flow.tsx) only renders what buildPages returns, so copy and order change here.
 */

/** A step's id: its answer key made hash-friendly (#who-needs-help, #how-soon, #grade-level). */
export type StepId = string;

export type Answer = string;
/** The answers by the legacy form's answer keys (who-needs-help, grade-level, how_soon, zipcode…). */
export type Answers = Record<string, Answer>;

/** Everything the chat keeps in session storage. Contact details are never stored. */
export type ChatState = { v: 2; form: string; answers: Answers };

/** A legacy panel condition (panels-registry renderWhen): all of a panel's conditions must hold. */
export type RenderWhen = { key: string; equals?: string; notEquals?: string; in?: ReadonlyArray<string> };

/** One legacy panel as a chat step (serializable: built on the server, sent to the page). */
export type FormStep = {
  kind: 'choice' | 'zip' | 'district' | 'partner' | 'name' | 'email' | 'phone';
  id: StepId;
  /** The legacy answer key the answer is saved and sent under. */
  key: string;
  /** The legacy panel id, for the same analytics as the standard form. */
  panelId: string;
  /** The legacy question, with {Subject}/{Type}/{Location} filled in (the first-question override applied). */
  question: string;
  options?: ReadonlyArray<{ value: string; label: string }>;
  renderWhen?: ReadonlyArray<RenderWhen>;
};

/** The legacy page as the chat's form. */
export type ChatForm = {
  /** Identifies the page and its questions, so a saved chat from another page starts over. */
  id: string;
  steps: ReadonlyArray<FormStep>;
  /** The page's assumed grade level (slug config assumedGradeLevel): counts as the grade-level answer. */
  assumedGradeLevel: string | null;
  /** The page's subject (URL or ?vtsubject=), sent on the lead as the standard form does. */
  subject: string | null;
  /** The subject as people read it ("SAT", "Math", "Computer Science"), or "" for the generic page. */
  subjectLabel: string;
};

/** What the page knows about the ZIP code's school districts (fetched once the ZIP is answered). */
export type DistrictLookup = {
  zip: string;
  /** Null while the lookup is still running. */
  districts: ReadonlyArray<SchoolDistrict> | null;
};

/** Switches read from the URL (?roadmap=1), so a split test can turn a page on without a deploy. */
export type Flags = {
  /** Show "Here's how this works" after the first answer. Off by default: the legacy form has no such page. */
  roadmap?: boolean;
};

export type Ctx = {
  answers: Answers;
  form: ChatForm;
  /** "My child" or "Someone else": the copy says "your student". */
  parent: boolean;
  /** The districts for the answered ZIP (undefined until the lookup for that ZIP has started). */
  lookup: DistrictLookup | undefined;
  flags: Flags;
};

/** A line of copy, or a marker for a card: [roadmap], [ratings], [partner]. */
type Line = string | ((ctx: Ctx) => string | null);
type Reply = Line | ReadonlyArray<Line>;

export type Option = {
  value: string;
  label: string;
  /** What the assistant says after this answer. */
  reply?: Reply;
};

export type InputKind = 'choice' | 'zip' | 'name' | 'email' | 'phone';

/** The contact answer keys: never saved to the session. */
export const CONTACT_KEYS: ReadonlyArray<string> = ['full-name', 'email', 'phone'];

export type Step = {
  id: StepId;
  key: string;
  kind: FormStep['kind'];
  /** The standard form's panel id, for analytics parity (trackFormStepComplete). */
  panelId: string;
  /** The question reads as a statement with one Continue (the partner school page): no number, no list. */
  statement?: boolean;
  /** What the assistant says, in order. The last line is the question. */
  lines: ReadonlyArray<Line>;
  input: InputKind;
  options?: ReadonlyArray<Option> | ((ctx: Ctx) => ReadonlyArray<Option>);
  /** Skip the step (already known, or not relevant). */
  skip?: (ctx: Ctx) => boolean;
  /** The step is relevant but its options aren't known yet (the district lookup is running). */
  loading?: (ctx: Ctx) => boolean;
  /** The visitor's bubble for an answer; defaults to the option label. */
  echo?: (answer: Answer, ctx: Ctx) => string;
  /** What the assistant says after any answer (an option's own reply wins). */
  reply?: Reply;
};

/** A contact step (name, email, phone): no message box, never saved. */
export const isContact = (step: Pick<Step, 'kind'>): boolean => step.kind === 'name' || step.kind === 'email' || step.kind === 'phone';

// ---------- Proof: only the site's approved figures ----------

export const PROOF = {
  rating: '4.9 out of 5 across 3.4M session ratings',
  trustpilot: 'Rated Excellent on Trustpilot, 13,500+ reviews',
  years: 'Tutoring since 2007',
} as const;

/** The three steps after the first answer: what happens, and a short, approved detail for each. */
export const ROADMAP = {
  heading: "Here's how this works.",
  steps: [
    { icon: 'chat', title: 'A few quick questions', note: 'About a minute, right here' },
    { icon: 'match', title: 'We match you with a tutor who fits', note: 'A personal education consultant calls you' },
    { icon: 'star', title: 'Your first session', note: 'Live, 1-on-1 and online' },
  ],
} as const;

// ---------- The legacy form's rules ----------

/** The grade levels the legacy form counts as K-12 for the school district panels (InteractiveForm isK12). */
const K12_GRADES: ReadonlyArray<string> = ['Elementary school', 'Middle school', 'High School'];

/** The school district options that aren't a public district (always offered, like the form). */
export const NON_PUBLIC_DISTRICTS: ReadonlyArray<SchoolDistrict> = [
  { id: 'private', name: 'Private School', isPublic: false },
  { id: 'other', name: 'Other', isPublic: false },
];

/** An answer as the legacy form reads it for its rules: the page's assumed grade level counts as answered. */
const valueOf = (ctx: Ctx, key: string): string | undefined => ctx.answers[key] ?? (key === 'grade-level' ? ctx.form.assumedGradeLevel ?? undefined : undefined);

/** The legacy renderWhen check (InteractiveForm evaluateRenderWhenWith): every condition must hold. */
export const meetsConditions = (conditions: ReadonlyArray<RenderWhen> | undefined, ctx: Ctx): boolean =>
  (conditions ?? []).every((condition) => {
    const value = valueOf(ctx, condition.key);
    if (condition.equals !== undefined && value !== condition.equals) return false;
    if (condition.notEquals !== undefined && value === condition.notEquals) return false;
    if (condition.in && (!value || !condition.in.includes(value))) return false;
    return true;
  });

/** The legacy form's K-12 check for the school district and partner school panels. */
export const isK12 = (ctx: Ctx): boolean => K12_GRADES.includes(valueOf(ctx, 'grade-level') ?? '') || ctx.answers['grade-level-hs-col'] === 'High School';

// ---------- Copy helpers ----------

const you = (ctx: Ctx): string => (ctx.parent ? 'your student' : 'you');

/** A legacy panel's shared header ("Let's finish up your profile…:") as a sentence. */
const asSentence = (text: string): string => text.trim().replace(/:$/, '.');

/** The finished district lookup for the answered ZIP, if there is one. */
const lookupFor = (ctx: Ctx): ReadonlyArray<SchoolDistrict> | undefined => {
  const zip = ctx.answers.zipcode;
  return ctx.lookup && zip && ctx.lookup.zip === zip && ctx.lookup.districts ? ctx.lookup.districts : undefined;
};

/** The district the visitor picked, from the lookup for their ZIP. */
export const pickedDistrict = (ctx: Ctx): SchoolDistrict | undefined => {
  const id = ctx.answers['school-district'];
  const districts = lookupFor(ctx);
  if (!id || !districts) return undefined;
  return districtOptionsFor(districts).find((district) => district.id === id);
};

/** The list the district question offers: the ZIP's public districts, then Private School and Other. */
export const districtOptionsFor = (districts: ReadonlyArray<SchoolDistrict>): SchoolDistrict[] => {
  const publicDistricts = districts.filter((district) => district.isPublic);
  const extras = NON_PUBLIC_DISTRICTS.filter((extra) => !districts.some((district) => district.id === extra.id));
  return [...publicDistricts, ...districts.filter((district) => !district.isPublic), ...extras];
};

/** Whether the ZIP's lookup found a public district (the form only asks the district question then). */
const hasPublicDistrict = (ctx: Ctx): boolean => Boolean(lookupFor(ctx)?.some((district) => district.isPublic));

// ---------- The steps ----------

/** One legacy panel as a chat step, with the chat's wording for the ZIP and contact pages. */
export const stepFor = (spec: FormStep, index: number): Step => {
  const base = { id: spec.id, key: spec.key, kind: spec.kind, panelId: spec.panelId };
  const conditional = (ctx: Ctx) => !meetsConditions(spec.renderWhen, ctx);
  // The roadmap follows the first answer, when ?roadmap=1 is on.
  const reply: Reply | undefined = index === 0 ? [(ctx) => (ctx.flags.roadmap ? ROADMAP.heading : null), (ctx) => (ctx.flags.roadmap ? '[roadmap]' : null)] : undefined;
  switch (spec.kind) {
    case 'choice':
      return { ...base, lines: [spec.question], input: 'choice', options: spec.options ?? [], skip: conditional, reply };
    case 'zip':
      return {
        ...base,
        lines: [(ctx) => `Almost done. We use your ZIP code to match time zones and find tutors near ${you(ctx)}.`, "What's your ZIP code?"],
        input: 'zip',
        skip: conditional,
        echo: (answer) => answer,
        reply,
      };
    case 'district':
      // The form's school district panel: for K-12 grades only, once there's a ZIP with a public district.
      return {
        ...base,
        lines: [spec.question || 'Select your school district:'],
        input: 'choice',
        skip: (ctx) => conditional(ctx) || !isK12(ctx) || !ctx.answers.zipcode || (lookupFor(ctx) !== undefined && !hasPublicDistrict(ctx)),
        loading: (ctx) => isK12(ctx) && Boolean(ctx.answers.zipcode) && lookupFor(ctx) === undefined,
        options: (ctx) => districtOptionsFor(lookupFor(ctx) ?? []).map((district) => ({ value: district.id, label: district.name })),
      };
    case 'partner':
      // The form's partner school panel: a public district the visitor picked partners with Varsity Tutors.
      // It has one answer ("Continue"), so it reads as a statement page with a Yes.
      return {
        ...base,
        statement: true,
        lines: ['[partner]', (ctx) => `${pickedDistrict(ctx)?.name ?? 'Your school district'} partners with Varsity Tutors 🎉`],
        input: 'choice',
        options: [{ value: 'continue', label: 'Continue' }],
        skip: (ctx) => {
          const district = pickedDistrict(ctx);
          return conditional(ctx) || !isK12(ctx) || !district || !district.isPublic;
        },
      };
    case 'name':
      return {
        ...base,
        // Social proof where it counts, then the legacy panel's own header, right before the details.
        lines: ['[ratings]', asSentence(spec.question || "Let's finish up your profile so we can find you the perfect tutor:"), "What's your full name?"],
        input: 'name',
        skip: conditional,
        echo: (answer) => answer,
      };
    case 'email':
      // Email may be left blank, as on the lead form.
      return { ...base, lines: ["What's your email?"], input: 'email', skip: conditional, echo: (answer) => answer || 'No email' };
    case 'phone':
      return { ...base, lines: ["What's the best number to reach you?"], input: 'phone', skip: conditional };
  }
};

/** The chat's steps for a form. */
export const stepsFor = (form: ChatForm): Step[] => form.steps.map(stepFor);

// ---------- The pages ----------

export type CardKind = 'roadmap' | 'ratings' | 'partner';

export type PageLine = { key: string; text: string } | { key: string; card: CardKind };

export const ctxFor = (state: ChatState, lookup: DistrictLookup | undefined, flags: Flags, form: ChatForm): Ctx => {
  const learner = state.answers['who-needs-help'];
  // "Someone else" (a grandparent, an older sibling…) reads like a parent: "your student".
  return { answers: state.answers, form, parent: learner === 'My child' || learner === 'Someone else', lookup, flags };
};

/** The switches in a page's query string. */
export const flagsFrom = (search: string): Flags => ({ roadmap: new URLSearchParams(search).get('roadmap') === '1' });

export const optionsFor = (step: Step, ctx: Ctx): ReadonlyArray<Option> => (typeof step.options === 'function' ? step.options(ctx) : step.options ?? []);

const resolve = (line: Line, ctx: Ctx): string | null => (typeof line === 'function' ? line(ctx) : line);

const resolveAll = (reply: Reply | undefined, ctx: Ctx): string[] => {
  if (!reply) return [];
  const lines: ReadonlyArray<Line> = typeof reply === 'string' || typeof reply === 'function' ? [reply] : reply;
  return lines.map((line) => resolve(line, ctx)).filter((line): line is string => Boolean(line));
};

export const echoFor = (step: Step, answer: Answer, ctx: Ctx): string => {
  if (step.echo) return step.echo(answer, ctx);
  return optionsFor(step, ctx).find((option) => option.value === answer)?.label ?? answer;
};

const replyFor = (step: Step, answer: Answer, ctx: Ctx): string[] => {
  const own = optionsFor(step, ctx).find((option) => option.reply && option.value === answer);
  return resolveAll(own?.reply ?? step.reply, ctx);
};

/** A line as a page line: plain text, or one of the cards. */
const lineFor = (key: string, text: string): PageLine => {
  const marker = /^\[(roadmap|ratings|partner)\]$/.exec(text);
  return marker ? { key, card: marker[1] as CardKind } : { key, text };
};

/**
 * The answer saved for a step, only if it still answers the question as it stands now: one of today's
 * options (an answer from an older version of a question doesn't count), or a five-digit ZIP. Anything
 * else counts as unanswered, so the form asks it again and nothing after it can be reached until it's
 * answered.
 */
export const answerFor = (step: Step, state: ChatState, ctx: Ctx): Answer | undefined => {
  const answer = state.answers[step.key];
  if (answer === undefined) return undefined;
  switch (step.input) {
    case 'choice':
      return optionsFor(step, ctx).some((option) => option.value === answer) ? answer : undefined;
    case 'zip':
      return /^\d{5}$/.test(answer) ? answer : undefined;
    case 'name':
      return answer.trim() ? answer : undefined;
    case 'email':
      // Blank is an answer: the lead form lets email be left empty.
      return answer;
    default:
      return undefined;
  }
};

/** Steps that apply to these answers, in order. */
export const activeSteps = (ctx: Ctx): Step[] => stepsFor(ctx.form).filter((step) => !step.skip?.(ctx));

/**
 * The chat as a paged form (one screen per page, like a typeform): each question gets a page, an
 * answer with something to say gets a statement page after it. Pages run up to the first unanswered
 * question, so the visitor can page back and forth over everything answered so far but never past the
 * question waiting for them. A `loading` page holds the place of a question whose options are still
 * being looked up (the school districts for a ZIP).
 */
export type Page =
  | {
      kind: 'question';
      key: string;
      step: Step;
      /** 1, 2, 3… across the question pages. */
      number: number;
      /** Lines before the question. */
      lead: PageLine[];
      question: string;
      answer: Answer | undefined;
    }
  | { kind: 'statement'; key: string; after: StepId; step: Step; lines: PageLine[] }
  | { kind: 'loading'; key: string; step: Step };

const botLines = (prefix: string, texts: ReadonlyArray<string>): PageLine[] => texts.map((text, i) => lineFor(`${prefix}-${i}`, text));

export const buildPages = (state: ChatState, lookup: DistrictLookup | undefined, flags: Flags, form: ChatForm): Page[] => {
  const ctx = ctxFor(state, lookup, flags, form);
  const pages: Page[] = [];
  let number = 0;
  for (const step of activeSteps(ctx)) {
    if (step.loading?.(ctx)) {
      pages.push({ kind: 'loading', key: `l-${step.id}`, step });
      return pages;
    }
    const lines = step.lines.map((line) => resolve(line, ctx)).filter((line): line is string => Boolean(line));
    const question = lines[lines.length - 1] ?? '';
    if (!step.statement) number += 1;
    const answer = answerFor(step, state, ctx);
    pages.push({ kind: 'question', key: `q-${step.id}`, step, number, lead: botLines(`${step.id}-lead`, lines.slice(0, -1)), question, answer });
    if (answer === undefined) return pages;
    const reply = replyFor(step, answer, ctx);
    if (reply.length) pages.push({ kind: 'statement', key: `s-${step.id}`, after: step.id, step, lines: botLines(`${step.id}-reply`, reply) });
  }
  return pages;
};

/**
 * The state with one answer added (under the step's legacy answer key). An earlier answer to a question
 * that no longer applies stays in the record but counts for nothing: answerFor checks it against today's
 * options, and leadAnswersFrom only sends answers to steps that are active.
 */
export const withAnswer = (state: ChatState, step: Pick<Step, 'key'>, answer: Answer): ChatState => ({
  ...state,
  answers: { ...state.answers, [step.key]: answer },
});

// ---------- The lead ----------

export type Contact = { name: string; email: string; phone: string };

/**
 * The `answers` record submitFormToCRM receives: the legacy form's keys and values, so a chat lead looks
 * exactly like a form lead to the CRM. Null while a question the page asks is unanswered.
 */
export const leadAnswersFrom = (state: ChatState, contact: Contact, lookup: DistrictLookup | undefined, form: ChatForm): Record<string, string> | null => {
  const ctx = ctxFor(state, lookup, {}, form);
  const answers: Record<string, string> = {};
  for (const step of activeSteps(ctx)) {
    // The partner page has nothing to send; the contact details come from the contact steps' fields.
    if (step.kind === 'partner' || isContact(step)) continue;
    const answer = answerFor(step, state, ctx);
    if (answer === undefined) return null;
    answers[step.key] = answer;
  }
  // The page's assumed grade level, as the legacy form seeds it (SAT/ACT pages: High School).
  if (!answers['grade-level'] && form.assumedGradeLevel) answers['grade-level'] = form.assumedGradeLevel;
  const district = pickedDistrict(ctx);
  if (district && answers['school-district']) answers['school-district-name'] = district.name;
  answers['full-name'] = contact.name.trim();
  answers.email = contact.email.trim();
  answers.phone = contact.phone;
  answers.consent = 'on';
  // The page's own subject (its URL or ?vtsubject=), only when it has one, as on the standard form.
  if (form.subject) answers.subject = form.subject;
  return answers;
};

/** A saved state, checked field by field (session storage is untrusted input). */
export const parseState = (raw: string | null, form: ChatForm): ChatState | null => {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    const { v, form: savedForm, answers } = value as Partial<ChatState>;
    // A chat saved on another page (other questions, another subject) starts over.
    if (v !== 2 || savedForm !== form.id || !answers || typeof answers !== 'object') return null;
    const keys = new Set<string>(form.steps.map((step) => step.key));
    const clean: Answers = {};
    for (const [key, answer] of Object.entries(answers)) {
      if (keys.has(key) && !CONTACT_KEYS.includes(key) && typeof answer === 'string') clean[key] = answer.slice(0, 200);
    }
    return { v: 2, form: form.id, answers: clean };
  } catch {
    return null;
  }
};

/** A new chat for a form. */
export const newState = (form: ChatForm): ChatState => ({ v: 2, form: form.id, answers: {} });
